/**
 * Agent Detail View
 *
 * Full view for a single agent including:
 * - Current task & status
 * - Budget controls
 * - Learning ledger
 * - Audit trail
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxTheme, raxButtonStyles, raxBadgeStyles } from "../styles/rax-theme.js";

// ============================================================================
// Types
// ============================================================================

export interface AgentTask {
  id: string;
  description: string;
  status: "active" | "paused" | "completed" | "failed";
  startedAt: string;
  checkpoint?: string;
  progress?: number; // 0-100
  budgetCents: number;
  spentCents: number;
  estimatedCompletionMinutes?: number;
}

export interface BudgetConfig {
  dailyLimitCents: number;
  perTaskLimitCents: number;
  totalLimitCents: number;
  alertThresholdPercent: number;
  pauseOnLimit: boolean;
}

export interface LedgerEntry {
  id: string;
  type: "preference" | "fact" | "context" | "skill" | "relationship";
  key: string;
  value: string;
  confidence: number;
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuditEntry {
  id: string;
  timestamp: string;
  action: string;
  details: string;
  costCents?: number;
  model?: string;
  tokensUsed?: number;
}

// ============================================================================
// Component
// ============================================================================

@customElement("rax-agent-detail")
export class RaxAgentDetail extends LitElement {
  static override styles = [
    raxTheme,
    raxButtonStyles,
    raxBadgeStyles,
    css`
      :host {
        display: block;
        background: var(--bg-primary);
        min-height: 100vh;
      }
      
      .agent-detail {
        max-width: 1200px;
        margin: 0 auto;
        padding: var(--space-6);
      }
      
      /* Header */
      .agent-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--space-6);
        padding-bottom: var(--space-4);
        border-bottom: 1px solid var(--border-color);
      }
      
      .agent-identity {
        display: flex;
        align-items: center;
        gap: var(--space-4);
      }
      
      .agent-avatar {
        width: 64px;
        height: 64px;
        border-radius: var(--radius-xl);
        background: var(--bg-secondary);
        border: 2px solid var(--rax-yellow);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 32px;
        box-shadow: 0 0 20px var(--accent-glow);
      }
      
      .agent-meta h1 {
        font-size: var(--font-2xl);
        margin: 0 0 var(--space-1) 0;
        color: var(--text-primary);
      }
      
      .agent-meta .status {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-sm);
        color: var(--text-secondary);
      }
      
      .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--success);
      }
      
      .status-dot.paused {
        background: var(--warning);
      }
      .status-dot.error {
        background: var(--error);
      }
      
      /* Tabs */
      .tabs {
        display: flex;
        gap: var(--space-1);
        margin-bottom: var(--space-6);
        border-bottom: 1px solid var(--border-color);
        padding-bottom: var(--space-1);
      }
      
      .tab {
        padding: var(--space-3) var(--space-4);
        background: transparent;
        border: none;
        font-size: var(--font-sm);
        font-weight: 500;
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        border-radius: var(--radius-md) var(--radius-md) 0 0;
        position: relative;
      }
      
      .tab:hover {
        color: var(--text-primary);
        background: var(--bg-hover);
      }
      
      .tab.active {
        color: var(--rax-yellow);
      }
      
      .tab.active::after {
        content: "";
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--rax-yellow);
      }
      
      /* Current Task Panel */
      .current-task {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        padding: var(--space-5);
        margin-bottom: var(--space-6);
      }
      
      .task-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: var(--space-4);
      }
      
      .task-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        margin: 0 0 var(--space-2) 0;
      }
      
      .task-checkpoint {
        font-size: var(--font-sm);
        color: var(--rax-yellow);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .task-actions {
        display: flex;
        gap: var(--space-2);
      }
      
      .progress-bar {
        height: 8px;
        background: var(--bg-elevated);
        border-radius: var(--radius-full);
        overflow: hidden;
        margin-bottom: var(--space-4);
      }
      
      .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, var(--rax-yellow), var(--rax-gold));
        border-radius: var(--radius-full);
        transition: width 0.5s ease;
      }
      
      .task-stats {
        display: flex;
        gap: var(--space-6);
      }
      
      .task-stat {
        flex: 1;
      }
      
      .task-stat-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: var(--space-1);
      }
      
      .task-stat-value {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .task-stat-value.budget {
        font-family: monospace;
      }
      
      .task-stat-value.warning {
        color: var(--warning);
      }
      
      .task-stat-value.danger {
        color: var(--error);
      }
      
      /* Budget Controls */
      .budget-section {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        padding: var(--space-5);
        margin-bottom: var(--space-6);
      }
      
      .budget-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--space-4);
      }
      
      .section-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .budget-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: var(--space-4);
      }
      
      .budget-item {
        padding: var(--space-4);
        background: var(--bg-elevated);
        border-radius: var(--radius-lg);
        border: 1px solid var(--border-color);
      }
      
      .budget-item label {
        display: block;
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-transform: uppercase;
        margin-bottom: var(--space-2);
      }
      
      .budget-item input {
        width: 100%;
        padding: var(--space-2) var(--space-3);
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-md);
        font-family: monospace;
      }
      
      .budget-item input:focus {
        outline: none;
        border-color: var(--rax-yellow);
        box-shadow: 0 0 0 2px var(--accent-glow);
      }
      
      .budget-toggle {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-4);
        background: var(--bg-elevated);
        border-radius: var(--radius-lg);
        border: 1px solid var(--border-color);
        cursor: pointer;
      }
      
      .toggle {
        width: 44px;
        height: 24px;
        background: var(--bg-primary);
        border-radius: var(--radius-full);
        position: relative;
        transition: background var(--transition-fast);
      }
      
      .toggle.on {
        background: var(--rax-yellow);
      }
      
      .toggle::after {
        content: "";
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        background: white;
        border-radius: 50%;
        transition: transform var(--transition-fast);
      }
      
      .toggle.on::after {
        transform: translateX(20px);
      }
      
      .toggle-text {
        font-size: var(--font-sm);
        color: var(--text-secondary);
      }
      
      /* Learning Ledger */
      .ledger-section {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        padding: var(--space-5);
        margin-bottom: var(--space-6);
      }
      
      .ledger-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }
      
      .ledger-entry {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: var(--space-4);
        background: var(--bg-elevated);
        border-radius: var(--radius-lg);
        border: 1px solid var(--border-color);
        transition: all var(--transition-fast);
      }
      
      .ledger-entry:hover {
        border-color: var(--rax-yellow);
      }
      
      .ledger-content {
        flex: 1;
      }
      
      .ledger-key {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--rax-yellow);
        margin-bottom: var(--space-1);
      }
      
      .ledger-value {
        font-size: var(--font-sm);
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .ledger-meta {
        display: flex;
        gap: var(--space-3);
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .confidence-bar {
        width: 60px;
        height: 4px;
        background: var(--bg-primary);
        border-radius: var(--radius-full);
        overflow: hidden;
      }
      
      .confidence-fill {
        height: 100%;
        background: var(--success);
        border-radius: var(--radius-full);
      }
      
      .ledger-actions {
        display: flex;
        gap: var(--space-2);
      }
      
      .ledger-action {
        padding: var(--space-1) var(--space-2);
        background: transparent;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        color: var(--text-tertiary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .ledger-action:hover {
        border-color: var(--text-secondary);
        color: var(--text-primary);
      }
      
      .ledger-action.delete:hover {
        border-color: var(--error);
        color: var(--error);
      }
      
      /* Audit Trail */
      .audit-section {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        padding: var(--space-5);
      }
      
      .audit-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        max-height: 400px;
        overflow-y: auto;
      }
      
      .audit-entry {
        display: flex;
        gap: var(--space-3);
        padding: var(--space-3);
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
      }
      
      .audit-time {
        color: var(--text-tertiary);
        font-family: monospace;
        font-size: var(--font-xs);
        min-width: 80px;
      }
      
      .audit-action {
        flex: 1;
        color: var(--text-primary);
      }
      
      .audit-action .tool {
        color: var(--rax-yellow);
        font-weight: 500;
      }
      
      .audit-cost {
        color: var(--text-tertiary);
        font-family: monospace;
        font-size: var(--font-xs);
      }
      
      /* Empty State */
      .empty-state {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }
      
      .empty-state .icon {
        font-size: 48px;
        margin-bottom: var(--space-3);
        opacity: 0.5;
      }
      
      /* Type badges */
      .type-badge {
        display: inline-block;
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
      }
      
      .type-badge.preference {
        background: rgba(139, 92, 246, 0.2);
        color: #a78bfa;
      }
      .type-badge.fact {
        background: rgba(59, 130, 246, 0.2);
        color: #60a5fa;
      }
      .type-badge.context {
        background: rgba(16, 185, 129, 0.2);
        color: #34d399;
      }
      .type-badge.skill {
        background: rgba(245, 158, 11, 0.2);
        color: #fbbf24;
      }
      .type-badge.relationship {
        background: rgba(236, 72, 153, 0.2);
        color: #f472b6;
      }
      
      /* Skills Grid */
      .skills-grid {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        margin-top: var(--space-4);
      }
      
      .skill-item {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
      }
      
      .skill-item:hover {
        border-color: var(--rax-yellow);
      }
      
      .skill-name {
        color: var(--text-primary);
        font-weight: 500;
      }
      
      .skill-remove {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        background: transparent;
        border: none;
        color: var(--text-tertiary);
        cursor: pointer;
        border-radius: var(--radius-sm);
        font-size: 12px;
        transition: all var(--transition-fast);
      }
      
      .skill-remove:hover {
        background: rgba(239, 68, 68, 0.2);
        color: var(--error);
      }
    `,
  ];

  @property({ type: String })
  agentId = "";

  @property({ type: String })
  agentName = "Agent";

  @property({ type: String })
  agentIcon = "⚡";

  @property({ type: Object })
  currentTask: AgentTask | null = null;

  @property({ type: Object })
  budgetConfig: BudgetConfig = {
    dailyLimitCents: 500, // $5
    perTaskLimitCents: 100, // $1
    totalLimitCents: 5000, // $50
    alertThresholdPercent: 80,
    pauseOnLimit: true,
  };

  @property({ type: Array })
  ledgerEntries: LedgerEntry[] = [];

  @property({ type: Array })
  auditEntries: AuditEntry[] = [];

  @state()
  private activeTab: "overview" | "skills" | "ledger" | "audit" | "settings" = "overview";

  @property({ type: Array })
  enabledSkills: string[] = [];

  override render() {
    return html`
      <div class="agent-detail">
        ${this.renderHeader()}
        ${this.renderTabs()}
        ${this.renderContent()}
      </div>
    `;
  }

  private renderHeader() {
    return html`
      <div class="agent-header">
        <div class="agent-identity">
          <div class="agent-avatar">${this.agentIcon}</div>
          <div class="agent-meta">
            <h1>${this.agentName}</h1>
            <div class="status">
              <span class="status-dot ${this.currentTask?.status || "active"}"></span>
              <span>${this.currentTask ? `Working: ${this.currentTask.description.slice(0, 40)}...` : "Idle"}</span>
            </div>
          </div>
        </div>
        <div class="header-actions">
          <button class="btn btn-primary" @click=${this.onChat}>
            ⚡ Chat
          </button>
        </div>
      </div>
    `;
  }

  private renderTabs() {
    return html`
      <div class="tabs">
        <button
          class="tab ${this.activeTab === "overview" ? "active" : ""}"
          @click=${() => (this.activeTab = "overview")}
        >
          Overview
        </button>
        <button
          class="tab ${this.activeTab === "skills" ? "active" : ""}"
          @click=${() => (this.activeTab = "skills")}
        >
          Skills
        </button>
        <button
          class="tab ${this.activeTab === "ledger" ? "active" : ""}"
          @click=${() => (this.activeTab = "ledger")}
        >
          Learning Ledger
        </button>
        <button
          class="tab ${this.activeTab === "audit" ? "active" : ""}"
          @click=${() => (this.activeTab = "audit")}
        >
          Audit Trail
        </button>
        <button
          class="tab ${this.activeTab === "settings" ? "active" : ""}"
          @click=${() => (this.activeTab = "settings")}
        >
          Settings
        </button>
      </div>
    `;
  }

  private renderContent() {
    switch (this.activeTab) {
      case "overview":
        return this.renderOverview();
      case "skills":
        return this.renderSkills();
      case "ledger":
        return this.renderLedger();
      case "audit":
        return this.renderAudit();
      case "settings":
        return this.renderSettings();
    }
  }

  private renderOverview() {
    return html`
      ${this.currentTask ? this.renderCurrentTask() : this.renderIdleState()}
      ${this.renderBudgetSection()}
    `;
  }

  private renderIdleState() {
    return html`
      <div class="current-task">
        <div class="empty-state">
          <div class="icon">💤</div>
          <p>Agent is idle. Start a conversation to give it something to do.</p>
        </div>
      </div>
    `;
  }

  private renderCurrentTask() {
    if (!this.currentTask) return nothing;

    const budgetPercent = (this.currentTask.spentCents / this.currentTask.budgetCents) * 100;
    const isWarning = budgetPercent >= 80;
    const isDanger = budgetPercent >= 95;

    return html`
      <div class="current-task">
        <div class="task-header">
          <div>
            <h2 class="task-title">${this.currentTask.description}</h2>
            ${
              this.currentTask.checkpoint
                ? html`
              <div class="task-checkpoint">
                <span>📍</span>
                <span>Next: ${this.currentTask.checkpoint}</span>
              </div>
            `
                : nothing
            }
          </div>
          <div class="task-actions">
            <button class="btn btn-secondary btn-sm" @click=${this.onPauseTask}>
              ⏸️ Pause
            </button>
            <button class="btn btn-secondary btn-sm" @click=${this.onEditBudget}>
              💰 Edit Budget
            </button>
          </div>
        </div>

        ${
          this.currentTask.progress !== undefined
            ? html`
          <div class="progress-bar">
            <div class="progress-fill" style="width: ${this.currentTask.progress}%"></div>
          </div>
        `
            : nothing
        }

        <div class="task-stats">
          <div class="task-stat">
            <div class="task-stat-label">Budget Used</div>
            <div class="task-stat-value budget ${isDanger ? "danger" : isWarning ? "warning" : ""}">
              ${this.formatCents(this.currentTask.spentCents)} / ${this.formatCents(this.currentTask.budgetCents)}
            </div>
          </div>
          <div class="task-stat">
            <div class="task-stat-label">Time Elapsed</div>
            <div class="task-stat-value">${this.formatDuration(this.currentTask.startedAt)}</div>
          </div>
          ${
            this.currentTask.estimatedCompletionMinutes
              ? html`
            <div class="task-stat">
              <div class="task-stat-label">Est. Remaining</div>
              <div class="task-stat-value">~${this.currentTask.estimatedCompletionMinutes} min</div>
            </div>
          `
              : nothing
          }
        </div>
      </div>
    `;
  }

  private renderBudgetSection() {
    return html`
      <div class="budget-section">
        <div class="budget-header">
          <h3 class="section-title">💰 Budget Controls</h3>
          <button class="btn btn-secondary btn-sm" @click=${this.onSaveBudget}>
            Save Changes
          </button>
        </div>

        <div class="budget-grid">
          <div class="budget-item">
            <label>Daily Limit</label>
            <input 
              type="text" 
              value=${this.formatCents(this.budgetConfig.dailyLimitCents)}
              @change=${(e: Event) => this.onBudgetChange("dailyLimitCents", e)}
            />
          </div>
          <div class="budget-item">
            <label>Per Task Limit</label>
            <input 
              type="text" 
              value=${this.formatCents(this.budgetConfig.perTaskLimitCents)}
              @change=${(e: Event) => this.onBudgetChange("perTaskLimitCents", e)}
            />
          </div>
          <div class="budget-item">
            <label>Monthly Limit</label>
            <input 
              type="text" 
              value=${this.formatCents(this.budgetConfig.totalLimitCents)}
              @change=${(e: Event) => this.onBudgetChange("totalLimitCents", e)}
            />
          </div>
          <div class="budget-item">
            <label>Alert At (%)</label>
            <input 
              type="number" 
              min="1" 
              max="100"
              value=${this.budgetConfig.alertThresholdPercent}
              @change=${(e: Event) => this.onBudgetChange("alertThresholdPercent", e)}
            />
          </div>
        </div>

        <div 
          class="budget-toggle" 
          @click=${() => (this.budgetConfig = { ...this.budgetConfig, pauseOnLimit: !this.budgetConfig.pauseOnLimit })}
        >
          <div class="toggle ${this.budgetConfig.pauseOnLimit ? "on" : ""}"></div>
          <span class="toggle-text">
            Auto-pause when budget limit reached
          </span>
        </div>
      </div>
    `;
  }

  private renderLedger() {
    return html`
      <div class="ledger-section">
        <div class="budget-header">
          <h3 class="section-title">🧠 Learning Ledger</h3>
          <button class="btn btn-secondary btn-sm" @click=${this.onAddLedgerEntry}>
            + Add Entry
          </button>
        </div>

        ${
          this.ledgerEntries.length === 0
            ? html`
                <div class="empty-state">
                  <div class="icon">🧠</div>
                  <p>No learned entries yet. Your agent will add things as it learns about you.</p>
                </div>
              `
            : html`
          <div class="ledger-list">
            ${this.ledgerEntries.map((entry) => this.renderLedgerEntry(entry))}
          </div>
        `
        }
      </div>
    `;
  }

  private renderLedgerEntry(entry: LedgerEntry) {
    return html`
      <div class="ledger-entry">
        <div class="ledger-content">
          <div class="ledger-key">
            <span class="type-badge ${entry.type}">${entry.type}</span>
            ${entry.key}
          </div>
          <div class="ledger-value">${entry.value}</div>
          <div class="ledger-meta">
            <span>Confidence:</span>
            <div class="confidence-bar">
              <div class="confidence-fill" style="width: ${entry.confidence * 100}%"></div>
            </div>
            <span>Source: ${entry.source}</span>
            <span>${this.formatDate(entry.updatedAt)}</span>
          </div>
        </div>
        <div class="ledger-actions">
          <button class="ledger-action" @click=${() => this.onEditLedgerEntry(entry)}>
            Edit
          </button>
          <button class="ledger-action delete" @click=${() => this.onDeleteLedgerEntry(entry)}>
            Delete
          </button>
        </div>
      </div>
    `;
  }

  private renderAudit() {
    return html`
      <div class="audit-section">
        <div class="budget-header">
          <h3 class="section-title">📋 Audit Trail</h3>
          <button class="btn btn-secondary btn-sm" @click=${this.onExportAudit}>
            Export
          </button>
        </div>

        ${
          this.auditEntries.length === 0
            ? html`
                <div class="empty-state">
                  <div class="icon">📋</div>
                  <p>No activity yet. All agent actions will be logged here.</p>
                </div>
              `
            : html`
          <div class="audit-list">
            ${this.auditEntries.map((entry) => this.renderAuditEntry(entry))}
          </div>
        `
        }
      </div>
    `;
  }

  private renderAuditEntry(entry: AuditEntry) {
    return html`
      <div class="audit-entry">
        <span class="audit-time">${this.formatTime(entry.timestamp)}</span>
        <span class="audit-action">
          ${this.formatAuditAction(entry)}
        </span>
        ${
          entry.costCents
            ? html`
          <span class="audit-cost">${this.formatCents(entry.costCents)}</span>
        `
            : nothing
        }
      </div>
    `;
  }

  private renderSkills() {
    return html`
      <div class="budget-section">
        <div class="budget-header">
          <h3 class="section-title">Skills & Capabilities</h3>
          <button class="btn btn-secondary btn-sm" @click=${this.onManageSkills}>
            Manage All Skills
          </button>
        </div>

        ${
          this.enabledSkills.length === 0
            ? html`
          <div class="empty-state">
            <div class="icon">⚡</div>
            <p>No skills enabled for this agent. Enable skills to extend your agent's capabilities.</p>
            <button class="btn btn-primary" @click=${this.onManageSkills}>
              Enable Skills
            </button>
          </div>
        `
            : html`
          <div class="skills-grid">
            ${this.enabledSkills.map(
              (skillId) => html`
              <div class="skill-item">
                <span class="skill-name">${this.formatSkillName(skillId)}</span>
                <button class="skill-remove" @click=${() => this.onDisableSkill(skillId)} title="Disable skill">
                  x
                </button>
              </div>
            `,
            )}
          </div>
        `
        }
      </div>
    `;
  }

  private renderSettings() {
    return html`
      <div class="budget-section">
        <h3 class="section-title">Agent Settings</h3>
        <p style="color: var(--text-secondary)">
          Coming soon: model selection, personality config, skill permissions
        </p>
      </div>
    `;
  }

  // Formatters
  private formatCents(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }

  private formatDuration(startTime: string): string {
    const start = new Date(startTime);
    const now = new Date();
    const minutes = Math.floor((now.getTime() - start.getTime()) / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  }

  private formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  private formatAuditAction(entry: AuditEntry): any {
    // Parse tool calls and format them nicely
    if (entry.action.includes("tool:")) {
      const tool = entry.action.split(":")[1];
      return html`Used <span class="tool">${tool}</span>: ${entry.details}`;
    }
    return entry.details || entry.action;
  }

  // Event handlers
  private onChat() {
    this.dispatchEvent(
      new CustomEvent("open-chat", { bubbles: true, detail: { agentId: this.agentId } }),
    );
  }

  private onPauseTask() {
    this.dispatchEvent(
      new CustomEvent("pause-task", { bubbles: true, detail: { agentId: this.agentId } }),
    );
  }

  private onEditBudget() {
    this.dispatchEvent(
      new CustomEvent("edit-task-budget", { bubbles: true, detail: { agentId: this.agentId } }),
    );
  }

  private onBudgetChange(field: keyof BudgetConfig, e: Event) {
    const input = e.target as HTMLInputElement;
    const value =
      field === "alertThresholdPercent"
        ? parseInt(input.value)
        : Math.round(parseFloat(input.value.replace("$", "")) * 100);
    this.budgetConfig = { ...this.budgetConfig, [field]: value };
  }

  private onSaveBudget() {
    this.dispatchEvent(
      new CustomEvent("save-budget", {
        bubbles: true,
        detail: { agentId: this.agentId, budget: this.budgetConfig },
      }),
    );
  }

  private onAddLedgerEntry() {
    this.dispatchEvent(
      new CustomEvent("add-ledger-entry", { bubbles: true, detail: { agentId: this.agentId } }),
    );
  }

  private onEditLedgerEntry(entry: LedgerEntry) {
    this.dispatchEvent(new CustomEvent("edit-ledger-entry", { bubbles: true, detail: { entry } }));
  }

  private onDeleteLedgerEntry(entry: LedgerEntry) {
    this.dispatchEvent(
      new CustomEvent("delete-ledger-entry", { bubbles: true, detail: { entryId: entry.id } }),
    );
  }

  private onExportAudit() {
    this.dispatchEvent(
      new CustomEvent("export-audit", { bubbles: true, detail: { agentId: this.agentId } }),
    );
  }

  private onManageSkills() {
    this.dispatchEvent(
      new CustomEvent("manage-skills", { bubbles: true, detail: { agentId: this.agentId } }),
    );
  }

  private onDisableSkill(skillId: string) {
    this.dispatchEvent(
      new CustomEvent("disable-skill", {
        bubbles: true,
        detail: { agentId: this.agentId, skillId },
      }),
    );
  }

  private formatSkillName(skillId: string): string {
    // Convert kebab-case or snake_case to Title Case
    return skillId.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-agent-detail": RaxAgentDetail;
  }
}
